import { useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

/** Настройка обзора, пережившая перезагрузку (какие колонки видны, как сгруппированы строки, что
 *  отмечено). Читаем `localStorage` один раз при монтировании, пишем на каждое изменение.
 *
 *  `parse` возвращает `undefined`, если сохранённое больше не годится (сменился формат, режим
 *  переименован) — тогда берём `initial`. Это НЕ маскирует поломку: отсутствующая или устаревшая
 *  настройка обзора — штатное состояние, а вот отказ самого хранилища (приватное окно, запрет
 *  сайту хранить данные) виден в консоли, а не проглатывается.
 *
 *  Отдельным хелпером, потому что копий этого кода было четыре и они уже разъехались: у одной
 *  был `try/catch`, у другой нет — и в приватном окне страница падала на чтении. */
export function usePersistedState<T>(
  key: string,
  initial: T,
  parse: (raw: string) => T | undefined,
  stringify: (v: T) => string,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return (raw === null ? undefined : parse(raw)) ?? initial;
    } catch (e) {
      console.warn(`[usePersistedState] ${key}: не прочитано из localStorage`, e);
      return initial;
    }
  });
  // кодек через ref: вызывающие передают стрелки прямо в аргументах, и в зависимостях эффекта
  // они меняли бы идентичность каждый рендер — то есть запись шла бы на каждый рендер
  const enc = useRef(stringify);
  enc.current = stringify;
  useEffect(() => {
    try {
      localStorage.setItem(key, enc.current(value));
    } catch (e) {
      console.warn(`[usePersistedState] ${key}: не записано в localStorage`, e);
    }
  }, [key, value]);
  return [value, setValue];
}
